import JSZip from 'jszip';
import { ElectronApi } from './electron.api';
import { textToMd5 } from '@/utils/md5.utils';
import { ensureDir, testAccess } from '@/utils/fs/fs-extra.utils';
import { writeImage, getImageBase64 } from '@/utils/fs/image.utils';
import { WatchfaceDeviceType } from '@/hooks';
import { ShapeType } from './shape';

export type WatchfaceWidgetCate = '背景' | '时间' | '日期' | '数据';
export const watchfaceWidgetCates: WatchfaceWidgetCate[] = [
  '背景',
  '时间',
  '日期',
  '数据'
];
export interface WatchfaceWidget {
  name: string;
  deviceShapes: string[];
  zip: string;
  preview: string;
  cate: WatchfaceWidgetCate;
  includeDeviceTypes: WatchfaceDeviceType[];
  shapeTypes: ShapeType[];
}

/**
 * 表盘表达式模版
 */
export class WidgetService {
  static LAYER_JSON = 'layer.json';
  static RESOURCES_JSON = 'resources.json';
  private url =
    'https://watchface-editor-release.cnbj1.mi-fds.com/watchface-editor-release/templates';
  private jsonFilename = 'watchface.widgets3.json';
  private previewDir = 'preview';
  private zipDir = 'zip';

  /**
   * 获取表达式模版数据
   * @returns
   */
  async getWidgets() {
    try {
      let data: WatchfaceWidget[] = [];

      // 开发环境下加载本地文件
      if (import.meta.env.DEV) {
        const path = ElectronApi.path;
        const fs = ElectronApi.fs;
        if (path && fs) {
          const appDir = (await ElectronApi.getAppInfo()).appPath;
          const jsonPath = path.join(appDir, 'widgets', this.jsonFilename);
          if (await testAccess(jsonPath)) {
            const jsonStr = await fs.promises.readFile(jsonPath, {
              encoding: 'utf8'
            });
            data = JSON.parse(jsonStr);
            // 开发环境下，将预览图转换为 base64
            for (const item of data) {
              if (item.preview) {
                const previewPath = path.join(appDir, 'widgets', item.preview);
                if (await testAccess(previewPath)) {
                  const previewBase64 = await getImageBase64(previewPath);
                  if (previewBase64) {
                    item.preview = previewBase64;
                  }
                }
              }
            }
            return data;
          }
        }
      }
      // 生产环境或本地文件不存在时，从 URL 加载
      const url = this.url + '/' + this.jsonFilename + '?_key=' + Math.random();
      data = await fetch(url).then((r) => r.json());
      data.forEach((item) => {
        if (item.preview) {
          item.preview = [this.url, item.preview].join('/');
        }
      });
      return data;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * 下载表达式模版，并解压缩到本地
   * @param zip
   * @param saveDir
   */
  async downloadZip(zipfile: string) {
    try {
      const path = ElectronApi.path;
      const fs = ElectronApi.fs;
      if (!fs || !path) return;
      // 模版样例保存目录
      const samplesDir = path.join(
        (await ElectronApi.getPath('userData')) ?? '',
        'Widgets'
      );

      // 模版目录
      const sampleDir = path.join(samplesDir, zipfile.replace('.zip', ''));
      if (await testAccess(sampleDir)) {
        const files = await fs.promises.readdir(sampleDir);
        if (files.length) return sampleDir;
      }

      await ensureDir(sampleDir);

      let zip: JSZip;

      // 开发环境下从本地加载 zip 包
      if (import.meta.env.DEV) {
        const appDir = (await ElectronApi.getAppInfo()).appPath;
        const zipPath = path.join(appDir, 'widgets', zipfile);
        if (await testAccess(zipPath)) {
          const zipBuffer = await fs.promises.readFile(zipPath);
          zip = await JSZip.loadAsync(zipBuffer);
        } else {
          // 本地文件不存在，回退到 URL 下载
          const url = this.url + '/' + zipfile + '?_key=' + Math.random();
          const fileBf = await fetch(url).then((r) => r.arrayBuffer());
          zip = await JSZip.loadAsync(fileBf);
        }
      } else {
        // 生产环境从 URL 下载
        const url = this.url + '/' + zipfile + '?_key=' + Math.random();
        const fileBf = await fetch(url).then((r) => r.arrayBuffer());
        zip = await JSZip.loadAsync(fileBf);
      }

      for (const item of Object.entries(zip.files)) {
        const [file, fileObj] = item;
        if (fileObj.dir) continue;
        const content = await fileObj.async('arraybuffer');
        const filename = path.join(sampleDir, file);
        await ensureDir(path.dirname(filename));
        await fs.promises.writeFile(filename, Buffer.from(content));
      }
      return sampleDir;
    } catch (error) {
      console.log(error);
    }
  }

  async getWidgetData(widgetDir: string) {
    const path = ElectronApi.path;
    const fs = ElectronApi.fs;
    if (!fs || !path) return;
    const layerJson = path.join(widgetDir, WidgetService.LAYER_JSON);
    const resourceJson = path.join(widgetDir, WidgetService.RESOURCES_JSON);
    const _getJson = async (filename: string) => {
      if (!(await testAccess(filename))) return;
      try {
        const str = await fs.promises.readFile(filename, { encoding: 'utf8' });
        return JSON.parse(str);
      } catch (error) {
        console.log(error);
        return;
      }
    };
    return {
      widget: (await _getJson(layerJson)) as StoreLayerItem | undefined,
      resources: (await _getJson(resourceJson)) as
        | { basename: string; base64: string }[]
        | undefined
    };
  }

  async export(
    widgetLayerStr: string,
    resources: { basename: string; base64: string }[],
    desc: Omit<WatchfaceWidget, 'zip'>
  ) {
    const path = ElectronApi.path;
    const fs = ElectronApi.fs;
    if (!fs || !path) return;

    const appDir = (await ElectronApi.getAppInfo()).appPath;
    const widgetsDir = path.join(appDir, 'widgets');
    await ensureDir(widgetsDir);

    // 保存预览图
    const savePreview = async () => {
      const data = desc.preview;
      const previewDir = path.join(widgetsDir, this.previewDir);
      await ensureDir(previewDir);
      const basename = textToMd5(data, true) + '.png';
      const name = path.join(previewDir, basename);
      await writeImage(name, data);
      return this.previewDir + '/' + basename;
    };

    // 获取表达式数据
    const getWidgetZipBuffer = async () => {
      const zip = new JSZip();
      zip.file(WidgetService.LAYER_JSON, widgetLayerStr);
      // 保存图片资源
      // 图片直接保存json格式
      zip.file(
        WidgetService.RESOURCES_JSON,
        JSON.stringify(resources, undefined, 2)
      );

      const res = await zip.generateAsync({ type: 'arraybuffer' });
      return res;
    };

    // 保存图片
    const _preview = await savePreview();
    desc.preview = _preview;
    const saveJson = async (zipfile: string) => {
      const jsonfile = path.join(widgetsDir, this.jsonFilename);
      const widgets = (await testAccess(jsonfile))
        ? ((await fs.promises
            .readFile(jsonfile, 'utf8')
            .then((r) => JSON.parse(r))) as WatchfaceWidget[])
        : [];

      const itemIndex = widgets.findIndex(
        (item) => item.zip == zipfile || item.name == desc.name
      );
      if (itemIndex < 0) {
        widgets.push({
          ...desc,
          zip: zipfile
        });
      } else {
        widgets[itemIndex] = { ...widgets[itemIndex], ...desc, zip: zipfile };
      }
      await fs.promises.writeFile(
        jsonfile,
        JSON.stringify(widgets, undefined, 2)
      );
    };
    // 获取zip buffer
    const zipBuffer = await getWidgetZipBuffer();
    const zipBasename =
      textToMd5(widgetLayerStr + JSON.stringify(resources), true) + '.zip';
    const _zipDir = path.join(widgetsDir, this.zipDir);
    await ensureDir(_zipDir);
    const _zipFilename = path.join(_zipDir, zipBasename);
    await fs.promises.writeFile(_zipFilename, Buffer.from(zipBuffer));
    await saveJson(this.zipDir + '/' + zipBasename);

    return _zipFilename;
  }
}
